import { isArray } from 'lodash';
import { mergeClientSettings } from './merge-settings';
import { PreconClientSettings } from './settings-types';
import { staticDefaultSettings } from './default-settings';
import { PreconClientSettingsJson, getSettingsJson } from './settings-json';
import { getClientSettingsFromStorages } from './browser-storage';
import { getClientSettingsFromMetaTags } from './settings-meta-tags';
import { getMeta } from '@precon-tools-suite-by-shared/browser-utils';

declare global {
    interface Window {
        preconClientSettings?: PreconClientSettings;
        getClientSettings: () =>
            | PreconClientSettings
            | Promise<PreconClientSettings>;
        logClientSettings: () => void;
    }
}

export type ClientSettingsSource = PreconClientSettingsJson | string | URL;

function isHostedByBackend(): boolean {
    return typeof window !== 'undefined' && getMeta('spa-host') === 'backend';
}

export async function fetchClientSettings(
    sources?: ClientSettingsSource | ClientSettingsSource[] | null | undefined,
    strict = false,
    maxDepth = 10
): Promise<PreconClientSettings> {
    sources ??= isHostedByBackend() ? [] : ['/settings.json'];
    if (!isArray(sources)) sources = [sources];

    const jsonSources = await Promise.all(
        sources.map((source: ClientSettingsSource) =>
            getSettingsJson(source, strict, maxDepth)
        )
    );

    return mergeClientSettings([
        staticDefaultSettings,
        ...jsonSources,
        getClientSettingsFromMetaTags(),
        getClientSettingsFromStorages(),
    ]) as PreconClientSettings;
}

const loadingClientSettings: {
    currentlyLoadingSettings: Promise<PreconClientSettings> | null;
} = {
    currentlyLoadingSettings: null,
};

export async function loadClientSettings(
    sources?: ClientSettingsSource | ClientSettingsSource[] | null | undefined,
    strict = false,
    maxDepth = 10
) {
    loadingClientSettings.currentlyLoadingSettings = fetchClientSettings(
        sources,
        strict,
        maxDepth
    );

    const settings = await loadingClientSettings.currentlyLoadingSettings;
    if (typeof window !== 'undefined') {
        window.preconClientSettings = settings;
    }

    console.log('Client settings loaded.');

    return settings;
}

export function getClientSettings():
    | PreconClientSettings
    | Promise<PreconClientSettings> {
    if (typeof window !== 'undefined' && window.preconClientSettings) {
        return window.preconClientSettings;
    }

    if (loadingClientSettings.currentlyLoadingSettings) {
        return loadingClientSettings.currentlyLoadingSettings;
    }

    return loadClientSettings();
}

export function logClientSettings() {
    const clientSettings = getClientSettings();
    if ('then' in clientSettings && typeof clientSettings.then === 'function') {
        clientSettings.then((clientSettings) =>
            console.log('preconClientSettings:', clientSettings)
        );
    } else {
        console.log('preconClientSettings:', clientSettings);
    }
}

if (typeof window !== 'undefined') {
    window.getClientSettings = getClientSettings;
    window.logClientSettings = logClientSettings;
}
